<template>
  <div>
    <div v-if="this.hasVerified" class="is-flex justify-content-center is-fullHeight">
        <div class="content padding-top justify-content-center">
          <h4>Hi {{this.formLogin.firstName}}, {{this.titleText}}.</h4>
          <p class="is-size-7 has-text-left">Follow these rules to create a strong password.</p>
          <ul class="content is-size-7 has-text-left">
            <li class="unstyled" v-for="rule in activeRules" :key="rule.property">
                <span v-if="failed.length" class="icon is-left">
                  <font-awesome-icon v-if="ruleOk(rule)" :icon="['fas', 'check']"/>
                  <font-awesome-icon v-else class="has-text-danger" :icon="['fas', 'ban']"/>
                </span>
              {{rule.message}}
            </li>
          </ul>
          <div class="reset-container-p4-round-white">
            <div class>
              <img src="@/assets/eScholar-logo-color.png" alt="eScholar Uniq-ID" width="50" height="50" />
            </div>
            <form>
              <div class="field">
                <div class="field-body">
                  <div class="field has-text-left">
                    <div class="control">
                      <label for="accountEmail" class="label">Email Address</label>
                      <input
                        id="accountEmail"
                        type="email"
                        class="input"
                        :class="{ 'is-danger': this.hasErrorEmail }"
                        v-model="email"
                        required
                        :disabled="hasParams"
                        @change.prevent="validateEmail"
                      />
                    </div>
                    <span v-if="this.emailMsg" class="help is-danger">{{ emailMsg }}</span>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-body">
                  <div class="field has-text-left">
                    <div class="control">
                      <label for="formCreateAccountPassword" class="label">Enter Password</label>
                      <input
                        id="formCreateAccountPassword"
                        type="password" autocomplete="off"
                        class="input"
                        :class="{ 'is-danger': this.hasErrorPassword }"
                        v-model="password"
                        required
                        @change.prevent="validatePassword('password')"
                      />
                    </div>
                    <span v-if="this.pwMsg" class="help is-danger">{{ pwMsg }}</span>
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-body">
                  <div class="field has-text-left">
                    <div class="control">
                      <label for="passwordConfirm" class="label">Verify Password</label>
                      <input
                        id="passwordConfirm"
                        type="password" autocomplete="off"
                        class="input"
                        :class="{ 'is-danger': this.hasErrorCheckPassword }"
                        v-model.lazy="checkPassword"
                        required
                        @change.prevent="validatePassword('checkPassword')"
                      />
                    </div>
                    <span v-if="this.chkPwMsg" class="help is-danger">{{ chkPwMsg }}</span>
                  </div>
                </div>
              </div>
              <div class="field">
                <button
                  type="button"
                  class="button is-primary is-fullwidth"
                  @click.prevent="formSubmit"
                >{{ submitText }}</button>
              </div>
              <div class="has-text-left">
                <router-link to="/login" class="btn-link">&lt; Back to Login</router-link>
              </div>
            </form>
          </div>
        </div>
    </div>
    <div class="has-text-center" v-else>
      <div class="no-result-container">
        <div class="icon">
          <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
        </div>
        <p></p>
        <h4>The link you used is invalid or expired.</h4>
        <h4>Please ask for a new password. click on the lost password link.</h4>
        <p>
        <router-link to="/lostpassword" class="btn-link">
          &lt; Back to Lost Password</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheck,
  faBan,
  faExclamation,
} from '@fortawesome/pro-solid-svg-icons';

import EmailUtils from '@/mixins/EmailUtils';
import PasswordUtils from '@/mixins/PasswordUtils';
import NotificationManager from '@/mixins/NotificationManager';


library.add(
  faCheck,
  faBan,
  faExclamation,
);

export default {
  mixins: [EmailUtils, PasswordUtils, NotificationManager],
  name: 'ResetPassword',
  props: [
    'verificationKey',
  ],
  data() {
    return {
      login: {},
      hasParams: false,
      hasVerified: false,
      hasErrorEmail: false,
      hasErrorPassword: false,
      hasErrorCheckPassword: false,
      message: null,
      email: '',
      password: '',
      checkPassword: '',
      titleText: 'Please set your password',
      submitText: 'Reset Password & Login',
      emailMsg: '',
      pwMsg: '',
      chkPwMsg: '',
      failedMsg: 'Rules failed: ',
      formErrorMsg: 'There were some errors. Please fix the errors highlighted in the fields below.',
      failed: [],
    };
  },
  async mounted() {
    const emailParam = this.$router.currentRoute.params.userEmail;
    if (emailParam) {
      this.email = emailParam;
      this.titleText = 'Please update your password';
      this.hasParams = true;
    }
    if (this.verificationKey) {
      // this.hasParams = true;
      this.verify();
    } else {
      this.signOff();
    }
    
  },
  methods: {
    ...mapActions({
      verifyToken: 'verifyToken',
      resetPassword: 'resetPassword',
      validateLogin: 'validateLogin',
      initProperties: 'propertiesModule/init',
      initFields: 'fieldLabelsModule/init',
    }),
    ...mapActions([
      'signOff',
    ]),
    async verify() {
      try {
        await this.verifyToken(this.verificationKey).then((response) => {
          this.login = response.data;
          if (this.login && this.login.status !== 'ACTIVE') {
            this.message = `There were some errors. Can not reset password for ${this.login.status.toLowerCase()} users.`;
            this.showNotification('danger', [this.message]);
          } else {
            this.formLogin = Object.assign({}, this.login);
            this.hasVerified = true;
          }
        });
      } catch (error) {
        if (error.response.status === 500) {
          this.message = 'Network Error';
        } else {
          this.message = `There were some errors. ${error.response.data.message}`;
        }
        this.showNotification('danger', [this.message]);
      }
    },
    async formSubmit() {
      this.resetErrors();
      this.hideNotification();
      if (!this.validateForm()) {
        this.showNotification('danger', [this.formErrorMsg]);
        return;
      }

      this.failed = this.rulesFailed(this.email, this.password);
      if (this.failed.length > 0) {
        this.pwMsg = 'Password is invalid.';
        this.hasErrorPassword = true;
        this.showNotification('danger', [this.formErrorMsg]);
        return;
      }

      this.login.email = this.email;
      this.login.password = this.password;
      this.login.verificationKey = this.verificationKey;

      try {
        await this.resetPassword(this.login);
        const response = await this.validateLogin({
          email: this.email,
          password: this.password,
        });

        await Promise.all([
          this.initProperties(),
          this.initFields(),
        ]);
        this.showNotification('success', ['Your password had been reset successfully']);
        this.$router.push({ path: '/' });
      } catch (error) {
        if (error.response.status === 400) {
          if (error.response.data.message.toLowerCase().includes('same as previous password')) {
            this.failed.push('Must be different from your previous password');
            this.hasErrorPassword = true;
          } else if (error.response.data.message.toLowerCase().includes('email or password is invalid')) {
            this.emailMsg = 'Email or token is invalid.';
            this.hasErrorEmail = true;
          }

          this.showNotification('danger', [this.formErrorMsg]);
        } else {
          this.message = `${error.response.status === 500
            ? 'Network Error'
            : error.response.data.message}`;
          this.showNotification('danger', [this.message]);
        }
      }
      window.scrollTo(0, 0);
    },
    validateForm() {
      this.validateEmail();
      this.validatePassword('password');
      this.validatePassword('checkPassword');
      return !this.hasErrorEmail
        && !this.hasErrorPassword
        && !this.hasErrorCheckPassword;
    },
    validateEmail() {
      if (!this.email) {
        this.emailMsg = 'Email address is required.';
        this.hasErrorEmail = true;
      } else if (!this.isWelformed(this.email)) {
        this.emailMsg = 'Email address is invalid.';
        this.hasErrorEmail = true;
      } else {
        this.emailMsg = '';
        this.hasErrorEmail = false;
      }
    },
    validatePassword(field) {
      if (field === 'password') {
        if (!this.password) {
          this.pwMsg = 'Password is required.';
          this.hasErrorPassword = true;
        } else {
          this.pwMsg = '';
          this.hasErrorPassword = false;
        }
      }
      if (field === 'checkPassword') {
        if (!this.checkPassword) {
          this.chkPwMsg = 'Verify New Password is required.';
          this.hasErrorCheckPassword = true;
        } else {
          this.chkPwMsg = '';
          this.hasErrorCheckPassword = false;
        }
      }
      if (!this.hasErrorCheckPassword && !this.hasErrorPassword) {
        this.resetErrors();
        if (!this.checkPasswordMatch()) {
          this.chkPwMsg = 'Password and Verify Password do not match.';
          this.hasErrorCheckPassword = true;
        }
      }
    },
    ruleOk(rule) {
      let lookup;
      if (this.failed.length > 0) {
        lookup = this.failed
          .find(r => r.trim().toLowerCase() === rule.message.trim().toLowerCase());
      }
      return !lookup;
    },
    checkPasswordMatch() {
      return this.password === this.checkPassword;
    },
    resetErrors() {
      this.hasErrorEmail = false;
      this.hasErrorPassword = false;
      this.hasCheckErrorPassword = false;
      this.emailMsg = '';
      this.pwMsg = '';
      this.chkPwMsg = '';
      this.failed = [];
    },
  },
};
</script>

<style lang="sass" scoped>
  .reset-container-p4-round-white
    border-radius: 4.8px
    padding: 1.5rem !important
    background-color: white
    width: 348px
  .unstyled
    list-style-type: none
  .justify-content-center
      -webkit-box-pack: center !important
      -ms-flex-pack: center !important
      justify-content: center !important
  .padding-top
    padding-top: 1rem
  .no-result-container
    padding: 6.5rem
</style>
